import { Outlet, Link, useSearchParams } from "react-router-dom";
import { HouseholdLayout } from '@/components/layouts/HouseholdLayout.jsx';
import { PageHeader } from '@/components/PageHeader';
import { CrudTable } from '@/components/CrudTable';
import { ExtraAction } from '@/components/ExtraAction';
import { useDeleteIngredientMutation, useGetIngredientsQuery } from "@/features/ingredients/api.js";
import { Button, Stack, Typography } from "@mui/joy";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Input } from '@mui/joy';
import SearchIcon from '@mui/icons-material/Search';
import { EmojiDisplay } from '@/components/EmojiDisplay';
import { useSubcategoryMap } from '@/features/categories/hooks/useSubcategoryMap';
import { useMediaQuery, useTheme } from '@mui/material';
import { AddNavigationButton } from '@/components/AddNavigationButton';
import { newIngredientSort, newIngredientFilters } from '@/features/ingredients/schema.js';
import { sortColumnOptions, DEFAULT_SORT_COLUMN, sortIngredients, filterIngredients, computeIngredientBounds } from '@/features/ingredients/filters.js';
import { parseRangeParam, parseSortParams } from '@/utils/filters';
import { SortMenu } from '@/components/SortMenu';
import { ControlsGrid } from '@/components/ControlsGrid';
import { SelectableButtonBar } from '@/components/SelectableButtonBar';
import { Box } from '@mui/joy';
import { IngredientFiltersButton } from '@/features/ingredients/components/IngredientFiltersDrawer';
import { BulkEditIngredientsDrawer } from '@/features/ingredients/components/BulkEditIngredientsDrawer';
import { ActiveCellProvider, EditableIngredientCell } from '@/features/ingredients/components/EditableIngredientCell';

const RANGE_FILTER_KEYS = {
  kcal: 'kcal', protein: 'protein', fat: 'fat', carbs: 'carbs', fiber: 'fiber', density: 'density',
  piece_weight: 'piece', slice_weight: 'slice', package_size: 'package', portion_size: 'portion',
};

// Tighter than the table's own default --TableCell-paddingX (8px): these
// columns are three-digit numbers at most, so the wide gutter around them was
// mostly unused whitespace that the name column could put to better use.
const NUMERIC_CELL_STYLE = { paddingLeft: '4px', paddingRight: '4px' };

export function IngredientsPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  // Category has its own three widths, narrower than the mobile cutoff (md)
  // that hides every other secondary column: below isCategoryVisible's
  // threshold there still isn't room even for the icon, so the whole column
  // drops out (see allColumns below); above it but below lg, the subcategory
  // name is dropped in favor of just the emoji -- there isn't room for both
  // without crowding the columns next to it.
  const isCategoryVisible = useMediaQuery(theme.breakpoints.up(1100));
  const isRecipesVisible = useMediaQuery(theme.breakpoints.up(1020));
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'));
  const ingredients = useGetIngredientsQuery({});
  const useDelete = useDeleteIngredientMutation;
  const [search, setSearch] = useState('');
  const { map: categoryMap, categories } = useSubcategoryMap();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkEditOpen, setBulkEditOpen] = useState(false);

  const toggleRow = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleAllVisible = (visibleIds) => {
    setSelectedIds((prev) => {
      const allSelected = visibleIds.length > 0 && visibleIds.every((id) => prev.has(id));
      const next = new Set(prev);
      visibleIds.forEach((id) => allSelected ? next.delete(id) : next.add(id));
      return next;
    });
  };

  const getCategoryName = useCallback((row) => {
    if (!row.subcategory_id) return '';
    const entry = categoryMap.get(String(row.subcategory_id));
    return entry?.category?.name || '';
  }, [categoryMap]);

  const getSubcategoryName = useCallback((row) => {
    if (!row.subcategory_id) return '';
    const entry = categoryMap.get(String(row.subcategory_id));
    return entry?.subcategory?.name || '';
  }, [categoryMap]);

  // Category cell's default display: the category emoji plus the subcategory
  // name, matching how SubcategorySelect itself labels an option. Below the
  // lg breakpoint there isn't room for the name alongside it, so getCategoryIcon
  // drops to just the emoji.
  const getCategoryDisplay = useCallback((row) => {
    if (!row.subcategory_id) return '';
    const entry = categoryMap.get(String(row.subcategory_id));
    if (!entry?.subcategory?.name) return '';
    return (
      <Stack direction="row" spacing={1} alignItems="center" sx={{ overflow: 'hidden' }}>
        {entry.category?.emoji && <EmojiDisplay emoji={entry.category.emoji} size={16} />}
        <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {entry.subcategory.name}
        </Box>
      </Stack>
    );
  }, [categoryMap]);

  const getCategoryIcon = useCallback((row) => {
    if (!row.subcategory_id) return '';
    const entry = categoryMap.get(String(row.subcategory_id));
    if (!entry?.category?.emoji) return '';
    return (
      <span title={entry.subcategory?.name} style={{ display: 'flex', justifyContent: 'center', paddingRight: '8px' }}>
        <EmojiDisplay emoji={entry.category.emoji} size={16} />
      </span>
    );
  }, [categoryMap]);

  const [filters, setFilters] = useState(() => {
    const defaults = newIngredientFilters();
    const categoriesParam = searchParams.get('categories');
    const subcategoriesParam = searchParams.get('subcategories');
    const unitsParam = searchParams.get('units');
    const commonParam = searchParams.get('common');
    return {
      ...defaults,
      categories: categoriesParam ? categoriesParam.split(',').filter(Boolean) : defaults.categories,
      subcategories: subcategoriesParam ? subcategoriesParam.split(',').filter(Boolean) : defaults.subcategories,
      preferredUnits: unitsParam ? unitsParam.split(',').filter(Boolean) : defaults.preferredUnits,
      commonOnly: commonParam === 'true',
      kcal: parseRangeParam(searchParams.get(RANGE_FILTER_KEYS.kcal)),
      protein: parseRangeParam(searchParams.get(RANGE_FILTER_KEYS.protein)),
      fat: parseRangeParam(searchParams.get(RANGE_FILTER_KEYS.fat)),
      carbs: parseRangeParam(searchParams.get(RANGE_FILTER_KEYS.carbs)),
      fiber: parseRangeParam(searchParams.get(RANGE_FILTER_KEYS.fiber)),
      density: parseRangeParam(searchParams.get(RANGE_FILTER_KEYS.density)),
      piece_weight: parseRangeParam(searchParams.get(RANGE_FILTER_KEYS.piece_weight)),
      slice_weight: parseRangeParam(searchParams.get(RANGE_FILTER_KEYS.slice_weight)),
      package_size: parseRangeParam(searchParams.get(RANGE_FILTER_KEYS.package_size)),
      portion_size: parseRangeParam(searchParams.get(RANGE_FILTER_KEYS.portion_size)),
    };
  });

  const [sort, setSort] = useState(() => parseSortParams(searchParams, sortColumnOptions, newIngredientSort()));

  // "All" is simply the absence of any category, matching how the recipes bar
  // behaves. Categories are stored by name because that is what the ingredient
  // filter compares against.
  const categoryOptions = useMemo(() => {
    const toggleCategory = (name) => setFilters((previous) => ({
      ...previous,
      categories: previous.categories.includes(name)
        ? previous.categories.filter((category) => category !== name)
        : [...previous.categories, name],
    }));

    return [
      {
        key: 'all',
        label: 'All',
        selected: filters.categories.length === 0,
        onClick: () => setFilters((previous) => ({ ...previous, categories: [] })),
      },
      ...categories.map((category) => ({
        key: category.id,
        label: (
          <Stack direction="row" spacing={1} alignItems="center">
            {category.emoji && <EmojiDisplay emoji={category.emoji} size={16} />}
            <span>{category.name}</span>
          </Stack>
        ),
        selected: filters.categories.includes(category.name),
        onClick: () => toggleCategory(category.name),
      })),
    ];
  }, [categories, filters.categories]);

  useEffect(() => {
    const params = {};
    if (filters.categories.length > 0) {
      params.categories = filters.categories.join(',');
    }
    if (filters.subcategories.length > 0) {
      params.subcategories = filters.subcategories.join(',');
    }
    if (filters.preferredUnits.length > 0) {
      params.units = filters.preferredUnits.join(',');
    }
    if (filters.commonOnly) {
      params.common = 'true';
    }
    if (sort.column) {
      params.sort = sort.column;
      if (sort.direction !== 'asc') {
        params.dir = sort.direction;
      }
    }
    if (filters.kcal) {
      params[RANGE_FILTER_KEYS.kcal] = filters.kcal.join('-');
    }
    if (filters.protein) {
      params[RANGE_FILTER_KEYS.protein] = filters.protein.join('-');
    }
    if (filters.fat) {
      params[RANGE_FILTER_KEYS.fat] = filters.fat.join('-');
    }
    if (filters.carbs) {
      params[RANGE_FILTER_KEYS.carbs] = filters.carbs.join('-');
    }
    if (filters.fiber) {
      params[RANGE_FILTER_KEYS.fiber] = filters.fiber.join('-');
    }
    if (filters.density) {
      params[RANGE_FILTER_KEYS.density] = filters.density.join('-');
    }
    if (filters.piece_weight) {
      params[RANGE_FILTER_KEYS.piece_weight] = filters.piece_weight.join('-');
    }
    if (filters.slice_weight) {
      params[RANGE_FILTER_KEYS.slice_weight] = filters.slice_weight.join('-');
    }
    if (filters.package_size) {
      params[RANGE_FILTER_KEYS.package_size] = filters.package_size.join('-');
    }
    if (filters.portion_size) {
      params[RANGE_FILTER_KEYS.portion_size] = filters.portion_size.join('-');
    }

    const trackedKeys = ['categories', 'subcategories', 'units', 'common', 'sort', 'dir', ...Object.values(RANGE_FILTER_KEYS)];
    if (Object.keys(params).length > 0) {
      setSearchParams(params, { replace: true });
    } else if (trackedKeys.some((key) => searchParams.has(key))) {
      setSearchParams({}, { replace: true });
    }
  }, [filters.categories, filters.subcategories, filters.preferredUnits, filters.commonOnly, sort.column, sort.direction, filters.kcal, filters.protein, filters.fat, filters.carbs, filters.fiber, filters.density, filters.piece_weight, filters.slice_weight, filters.package_size, filters.portion_size, setSearchParams, searchParams]);

  const bounds = useMemo(() => computeIngredientBounds(ingredients.data?.data || []), [ingredients.data]);
  const filteredIngredients = useMemo(
    () => filterIngredients(ingredients.data?.data || [], filters, getCategoryName, getSubcategoryName),
    [ingredients.data, filters, getCategoryName, getSubcategoryName]
  );
  const sortedIngredients = useMemo(
    () => sortIngredients(filteredIngredients, sort, getCategoryName, getSubcategoryName),
    [filteredIngredients, sort, getCategoryName, getSubcategoryName]
  );

  // Memoised because CrudTable keys its filter/sort caches off the columns
  // array: a fresh array each render would rebuild them, and re-render every
  // row, on every keystroke.
  const columns = useMemo(() => {
    const numericColumn = (field, label) => (
      {
        label,
        getter: (row) => <EditableIngredientCell ingredient={row} field={field} type="number" />,
        hideOnMobile: true,
        width: '72px',
        cellStyle: NUMERIC_CELL_STYLE,
      }
    );

    const allColumns = [
      {
        label: 'Name',
        textValue: (row) => row.name || row.id,
        getter: (row) => <EditableIngredientCell ingredient={row} field="name" type="text" editable={!isMobile} />,
        cellStyle: { minWidth: '200px' },
      },
      ...(isCategoryVisible ? [{
        label: 'Category',
        getter: (row) => (
          <EditableIngredientCell
            ingredient={row}
            field="subcategory_id"
            type="select"
            displayValue={isLargeScreen ? getCategoryDisplay(row) : getCategoryIcon(row)}
          />
        ),
        width: isLargeScreen ? '170px' : '80px',
        cellStyle: isLargeScreen ? undefined : NUMERIC_CELL_STYLE,
      }] : []),
      {
        label: 'Common',
        getter: (row) => <Box sx={{ pr: 1}}><EditableIngredientCell ingredient={row} field="common" type="checkbox" /></Box>,
        hideOnMobile: true,
        width: '90px',
      },
      ...(isRecipesVisible ? [{
        label: 'Recipes',        
        getter: (row) => (
          <Typography
            level="body-sm"
            title={`Matched in ${row.recipe_match_count} recipe${row.recipe_match_count === 1 ? '' : 's'}`}
          >
            {row.recipe_match_count}
          </Typography>
        ),
        textValue: (row) => String(row.recipe_match_count),
        hideOnMobile: true,
        width: '80px',
        cellStyle: NUMERIC_CELL_STYLE,
      }] : []),
      numericColumn('kcal', 'Kcal'),
      numericColumn('fat', 'Fat'),
      numericColumn('carbs', 'Carbs'),
      numericColumn('protein', 'Protein'),
      numericColumn('fiber', 'Fiber'),
      numericColumn('package_size', 'Package'),
      numericColumn('portion_size', 'Portion'),
    ];

    return isMobile ? allColumns.filter(col => !col.hideOnMobile) : allColumns;
  }, [getCategoryDisplay, getCategoryIcon, isMobile, isCategoryVisible, isRecipesVisible, isLargeScreen]);

  const options = useMemo(() => ({
    names: {
      singular: 'ingredient',
      plural: 'ingredients',
    },

    columns,

    options: {
      searchable: false,
      externalSearch: true,
      virtualize: true,
    },

    actions: {
      noPrimary: true,
      extra: [{
        type: 'open',
        render: (row) => <ExtraAction to={row.id} tooltip="Open ingredient" icon={<OpenInNewIcon fontSize="small" />} />,
      }],
      delete: useDelete,
    },
  }), [columns, useDelete]);

  return (
    <HouseholdLayout addAction={isMobile && <AddNavigationButton to="create" />}>
      <Stack gap={2} direction="column">
        <PageHeader title="Ingredients">
          <Input
            sx={{ width: {xs: '100%', md: '250px'} }}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search"
            startDecorator={<SearchIcon/>}
          />
          {!isMobile &&<Link to="create">
            <Button startDecorator={<AddIcon/>} fullWidth={isMobile}>Add Ingredient</Button>
          </Link>}
        </PageHeader>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, flexWrap: { xs: 'nowrap', sm: 'wrap' }, gap: 2, alignItems: { xs: 'stretch', sm: 'flex-end' } }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <SelectableButtonBar options={categoryOptions} />
          </Box>
          <ControlsGrid>
            <SortMenu
              sort={sort}
              setSort={setSort}
              columnOptions={sortColumnOptions}
              defaultColumn={DEFAULT_SORT_COLUMN}
              sx={{ width: '100%' }}
            />
            <IngredientFiltersButton
              filters={filters}
              setFilters={setFilters}
              bounds={bounds}
              resultsCount={filteredIngredients.length}
              sx={{ width: '100%' }}
            />
          </ControlsGrid>
        </Box>
        {selectedIds.size > 0 && (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            gap={2}
            sx={{ pl: 2.25, pr: 1, py: 0.75, mb: -0.5, borderRadius: 'sm', backgroundColor: 'var(--joy-palette-background-level1)' }}
          >
            <Typography level="body-sm" fontWeight="lg">{selectedIds.size} selected</Typography>
            <Stack direction="row" gap={0.5}>
              <Button variant="plain" color="neutral" size="sm" onClick={() => setSelectedIds(new Set())}>
                Clear
              </Button>
              <Button
                variant="plain"
                color="primary"
                size="sm"
                startDecorator={<EditIcon sx={{ fontSize: 18 }} />}
                onClick={() => setBulkEditOpen(true)}
              >
                Edit
              </Button>
            </Stack>
          </Stack>
        )}
        <ActiveCellProvider>
          <CrudTable
            {...options}
            externalSearch={search}
            list={{ isLoading: ingredients.isLoading, error: ingredients.error, data: { data: sortedIngredients } }}
            selection={{ selectedIds, onToggleRow: toggleRow, onToggleAll: toggleAllVisible }}
          />
        </ActiveCellProvider>
      </Stack>
      <BulkEditIngredientsDrawer
        open={bulkEditOpen}
        onClose={() => setBulkEditOpen(false)}
        selectedIds={selectedIds}
        onApplied={() => setSelectedIds(new Set())}
      />
      <Outlet />
    </HouseholdLayout>
  );
}
